import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMarkUnitRead, useUnitInboxFor } from "../../../../features/unitNotifications/queries";
import { TONE_ACCENT, TONE_CLASS, ago, typeMeta, type UnitNotification, notificationTitle } from "../../../../features/unitNotifications/unitNotifications";
import NotificationDetail from "../../../../features/unitNotifications/NotificationDetail";
import { revealUnit } from "../../../../features/unitNotifications/revealUnit";
import { unreadLabel } from "../../../../features/unitNotifications/unitBell";
import UnitNotificationsModal from "./optionsIcons/modals/UnitNotificationsModal";
import { TONE_COLOR } from "./indicators";
import Tooltip from "../../../../components/ui/Tooltip";

interface Props {
  deviceId: string;
  vehicleId?: string | null;
  unitName: string;
  isDarkMode: boolean;
  /** «row»: sólo el indicador, junto a los semáforos de la fila. «card»: la campanita que abre la lista. */
  variant: "row" | "card";
}

const PANEL_W = 320;
// Alto que se reserva para decidir si abre hacia arriba: encabezado + 5 filas + pie.
const PANEL_H_GUESS = 400;

/**
 * Campanita de una unidad (Daniel, 26-oct): ver sus notificaciones sin
 * extender la tarjeta. Abre un popover anclado a la campanita, que crece desde
 * ella (no desde el centro) para que se lea de dónde salió; en las filas de
 * abajo de la lista abre hacia arriba.
 */
export default function UnitBell({ deviceId, vehicleId, unitName, isDarkMode, variant }: Props) {
  const { data } = useUnitInboxFor(deviceId, vehicleId);
  const markRead = useMarkUnitRead();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [history, setHistory] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number; origin: string } | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  // Un solo «pop» del número cuando llega una nueva: confirma que llegó algo
  // sin quedarse parpadeando en la lista.
  const prev = useRef(unread);
  const [pop, setPop] = useState(0);
  useEffect(() => {
    if (unread > prev.current) setPop((k) => k + 1);
    prev.current = unread;
  }, [unread]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(PANEL_W, window.innerWidth - 32);
      // Alineado al borde derecho de la campanita, pero dentro de la pantalla
      // con 16 px de margen: en la fila compacta la campanita queda a ~250 px
      // del borde izquierdo y 320 px a su izquierda se salían (medido: x = -33).
      const left = Math.min(Math.max(16, r.right - width), window.innerWidth - width - 16);
      const up = r.bottom + 6 + PANEL_H_GUESS > window.innerHeight && r.top > PANEL_H_GUESS;
      // Crece desde la campanita aunque el panel se haya corrido para caber.
      const origin = `${Math.round(r.left + r.width / 2 - left)}px ${up ? "100%" : "0"}`;
      setPos(up ? { bottom: window.innerHeight - r.top + 6, left, width, origin } : { top: r.bottom + 6, left, width, origin });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  // Entrada con transición (no keyframes): se puede interrumpir si se cierra a medio abrir.
  useEffect(() => {
    if (!open || !pos) return setShown(false);
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open, pos]);

  // Cada vez que se abre, la lista empieza cerrada: una desplegada de la vez anterior confunde.
  useEffect(() => {
    if (!open) setExpanded(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // La fila sólo informa (Daniel, 27-oct): abrir la lista desde la fila
  // comprimida era raro, porque la fila ya tiene su propio clic (desplegar) y
  // la lista quedaba colgando de una unidad sin su tarjeta. Tocar el indicador
  // despliega la tarjeta como el resto de la fila; ahí está la campanita.
  const tip = unread
    ? `${unread} ${unread === 1 ? "notificación" : "notificaciones"} sin leer`
    : items.length ? "Notificaciones al día" : "Sin notificaciones";
  if (variant === "row") {
    return (
      <Tooltip content={tip} delay={150} position="top" className="!flex !w-auto shrink-0 items-center">
        {/* Columna fija de 36 px (lo que mide la pastilla con «9+»): sin ella,
            los semáforos se corrían en las filas con avisos. */}
        <span className="shrink-0 w-9 h-5 flex items-center" aria-label={tip} role="img">
          {unread ? (
            // Pastilla en vez de un número rojo sobre una campana roja: a 16 px
            // el número tapaba el ícono y se leían como una mancha.
            <span
              key={pop}
              className={`h-[18px] pl-1 pr-1.5 flex items-center gap-0.5 rounded-full bg-red-500/15 text-red-500 text-[11px] font-semibold tabular-nums ${pop ? "animate-starPop motion-reduce:animate-none" : ""}`}
            >
              <i className="ri-notification-3-fill text-xs leading-none" aria-hidden="true" />
              {unreadLabel(unread)}
            </span>
          ) : (
            // Sin pendientes: gris de «sin dato», como los demás semáforos.
            <i className="ri-notification-3-line text-sm leading-none" style={{ color: TONE_COLOR.gray }} aria-hidden="true" />
          )}
        </span>
      </Tooltip>
    );
  }

  const toggle = (e: React.SyntheticEvent) => {
    // La fila y la tarjeta tienen su propio clic (expandir / seleccionar).
    e.stopPropagation();
    setOpen((v) => !v);
  };
  // Tocar una notificación la despliega con sus detalles (como en Mapon) y la
  // marca leída; seleccionar la unidad queda en «Ver en el mapa».
  const onItem = (n: UnitNotification) => {
    if (!n.read) markRead.mutate({ ids: [n.id] });
    setExpanded((id) => (id === n.id ? null : n.id));
  };
  const onMap = () => {
    revealUnit(deviceId);
    setOpen(false);
  };
  const unreadIds = items.filter((n) => !n.read).map((n) => n.id);
  const label = unread ? `Notificaciones de ${unitName}: ${unread} sin leer` : `Notificaciones de ${unitName}`;
  const status = unread ? `${unread} sin leer` : items.length ? "Al día" : "Sin notificaciones";

  const badge = unread > 0 && (
    <span
      key={pop}
      className={`min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-4 text-center tabular-nums ${pop ? "animate-starPop motion-reduce:animate-none" : ""}`}
    >
      {unreadLabel(unread)}
    </span>
  );

  // Es la única entrada a la lista: con pendientes se tiñe entera, no sólo el
  // número, para que se encuentre de un vistazo en el encabezado de la tarjeta.
  const trigger = (
    <Tooltip content={tip} delay={150} position="top" disabled={open} className="!flex !w-auto shrink-0">
    <button
      ref={triggerRef as React.RefObject<HTMLButtonElement>}
      type="button"
      aria-label={label}
      aria-expanded={open}
      onClick={toggle}
      className={`relative shrink-0 w-7 h-7 flex items-center justify-center rounded-md border transition-[background-color,transform] duration-150 active:scale-95 ${
        unread
          ? "border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20"
          : isDarkMode
            ? "border-dark-border text-gray-400 hover:bg-dark-card"
            : "border-gray-200 text-gray-500 hover:bg-gray-100"
      } ${open ? (isDarkMode ? "ring-1 ring-white/20" : "ring-1 ring-gray-300") : ""}`}
    >
      <i className={`${unread ? "ri-notification-3-fill" : "ri-notification-3-line"} text-base`} aria-hidden="true" />
      {badge && <span className="absolute -top-1.5 -right-1.5">{badge}</span>}
    </button>
    </Tooltip>
  );

  const panel = isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-white border-gray-200 text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const line = isDarkMode ? "border-dark-border" : "border-gray-200";

  return (
    <>
      {trigger}
      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={label}
          // React propaga los eventos a través del portal: sin esto, un clic aquí expande la fila.
          onClick={(e) => e.stopPropagation()}
          style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, transformOrigin: pos.origin }}
          className={`fixed z-[60] border rounded-xl shadow-xl overflow-hidden ${panel} transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-opacity ${
            shown ? "opacity-100 scale-100" : "opacity-0 scale-[0.96] motion-reduce:scale-100"
          }`}
        >
          <div className={`flex items-baseline justify-between gap-2 px-3 py-2 border-b ${line}`}>
            <span className="text-sm font-semibold font-mono truncate">{unitName}</span>
            {items.length > 0 && <span className={`text-xs shrink-0 ${unread ? "text-red-500" : muted}`}>{status}</span>}
          </div>
          <ul className={`max-h-80 overflow-y-auto divide-y ${isDarkMode ? "divide-dark-border" : "divide-gray-100"}`}>
            {items.length === 0 && (
              <li className="px-4 py-7 text-center">
                <span className={`mx-auto mb-2 w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-white/5" : "bg-gray-100"} ${muted}`}>
                  <i className="ri-notification-3-line text-lg" aria-hidden="true" />
                </span>
                <span className="block text-xs font-medium">Sin notificaciones recientes</span>
                <span className={`block mt-0.5 text-[11px] ${muted}`}>Aquí aparecerán los avisos de esta unidad.</span>
              </li>
            )}
            {items.map((n) => {
              const meta = typeMeta(n.type);
              return (
                <li key={n.id} className={`relative ${expanded === n.id ? `${isDarkMode ? "bg-white/[0.06]" : "bg-gray-100"} before:absolute before:left-0 before:inset-y-0 before:w-[3px] ${TONE_ACCENT[meta.tone]}` : ""}`}>
                  <button
                    type="button"
                    onClick={() => onItem(n)}
                    aria-expanded={expanded === n.id}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 transition-colors ${isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50"} ${n.read && expanded !== n.id ? "opacity-70" : ""}`}
                  >
                    <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${TONE_CLASS[meta.tone]}`}>
                      <i className={`${meta.icon} text-sm`} aria-hidden="true" />
                    </span>
                    <span className={`min-w-0 flex-1 text-xs truncate ${n.read ? "" : "font-semibold"}`}>{notificationTitle(n)}</span>
                    <span className={`shrink-0 text-[11px] tabular-nums ${muted}`}>{ago(n.created_at)}</span>
                    {!n.read && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500" aria-label="Sin leer" />}
                    <i className={`ri-arrow-down-s-line shrink-0 text-sm ${muted} transition-transform duration-200 ${expanded === n.id ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {expanded === n.id && <NotificationDetail n={n} isDarkMode={isDarkMode} onMap={onMap} />}
                </li>
              );
            })}
          </ul>
          <div className={`flex items-center justify-between gap-2 px-3 py-2 border-t text-xs ${line}`}>
            {items.length > 0 && (
              <button
                type="button"
                disabled={!unreadIds.length}
                onClick={() => markRead.mutate({ ids: unreadIds })}
                className="text-primary-500 hover:underline disabled:opacity-40 disabled:no-underline"
              >
                Marcar leídas
              </button>
            )}
            {vehicleId && (
              <button type="button" onClick={() => { setOpen(false); setHistory(true); }} className={`ml-auto flex items-center gap-0.5 hover:underline ${muted}`}>
                Historial <i className="ri-arrow-right-s-line" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
      {history && vehicleId && (
        <span onClick={(e) => e.stopPropagation()}>
          <UnitNotificationsModal vehicleId={vehicleId} unitName={unitName} isDarkMode={isDarkMode} onClose={() => setHistory(false)} />
        </span>
      )}
    </>
  );
}

